import { Link, Navigate, useLocation } from 'react-router';
import { Suspense } from 'react';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useAuth } from '@/hooks/useAuth';
import { signedOutPage } from '@/lib/safeRedirect';
import { useAllowedAdminRoutes, canSeeRoute, admitsAdminConsole } from '@/hooks/useAllowedAdminRoutes';
import type { AdminRouteKey } from '@/lib/adminRouteRoles';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { DEMO_MODE } from '@/lib/demoMode';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
  /**
   * The admin page this guards. The server decides who may open it
   * (allowed_admin_routes(), #894); omitted only on the /admin layout shell,
   * whose nested pages each carry their own key.
   */
  routeKey?: AdminRouteKey;
}

// Extracted so the fallback reference is stable (not recreated on every render).
const AdminSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

/**
 * Signed in, but not with an account that can open the admin console. This
 * used to flash a toast and bounce through /auth to that account's own home
 * page, so an owner whose phone was signed in as a helper tapped a report
 * email and landed on the helper dashboard with no idea why (1 Nov). Say
 * which account it is, and offer to switch: signing out keeps this page as
 * the place to come back to.
 */
function NeedsAdminAccount({ email, returnTo, onSwitch }: { email: string | null; returnTo: string; onSwitch: (returnTo: string) => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-sm space-y-4 text-center">
        <h1 className="h-page">This page needs an admin account</h1>
        <p className="text-muted-foreground">
          {email ? <>You are signed in as <strong className="text-foreground break-all">{email}</strong>, which cannot open it.</> : 'The account you are signed in with cannot open it.'}
        </p>
        <Button className="w-full" onClick={() => onSwitch(returnTo)}>Sign in with a different account</Button>
        <Button variant="ghost" className="w-full" asChild><Link to="/">Go to my home page</Link></Button>
      </div>
    </div>
  );
}

export function ProtectedAdminRoute({ children, routeKey }: ProtectedAdminRouteProps) {
  // ── ALL hooks must appear here, before any conditional return ─────────────
  const { isAdmin, loading } = useAdminAuth();
  const allowed = useAllowedAdminRoutes();
  const { user, signOut } = useAuth();
  const location = useLocation();
  // ─────────────────────────────────────────────────────────────────────────

  // DEMO MODE: Skip admin auth checks
  if (DEMO_MODE) {
    // Wrap in local Suspense so lazy admin chunks don't propagate to the
    // top-level Suspense in App.tsx (which would unmount the entire route
    // tree and reset useAdminAuth's loading state).
    return <Suspense fallback={<AdminSpinner />}>{children}</Suspense>;
  }

  if (loading) {
    return <AdminSpinner />;
  }

  // Console admission (#894): legacy staff, or anyone the server grants at
  // least one admin route (how `marketing` enters without STAFF_ROLES).
  if (!isAdmin) {
    // isLoading, not isPending: with no signed-in user the query is disabled
    // and stays pending forever, which must redirect, not spin.
    if (allowed.isLoading) return <AdminSpinner />;
    // Signed out (a report email's link opened in a fresh browser): sign in,
    // then come back to this page. "Access denied" is for a signed-in person.
    if (!user) return <Navigate to={signedOutPage(location.pathname + location.search)} replace />;
    if (!admitsAdminConsole(allowed)) {
      return <NeedsAdminAccount email={user.email ?? null} returnTo={location.pathname + location.search}
        onSwitch={(returnTo) => void signOut({ returnTo })} />;
    }
  }

  // Page access comes from the server's route matrix, not a client list.
  // Deny while unknown: pending shows the spinner, an error offers a retry.
  if (routeKey) {
    if (allowed.isPending) return <AdminSpinner />;
    if (allowed.isError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center space-y-3">
            <h1 className="h-page">Couldn't confirm your access</h1>
            <p className="text-muted-foreground">Check your connection and try again.</p>
            <Button variant="outline" onClick={() => void allowed.refetch()}>Retry</Button>
          </div>
        </div>
      );
    }
    if (!canSeeRoute(allowed, routeKey)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center">
            <h1 className="h-page mb-2">Access Denied</h1>
            <p className="text-muted-foreground">
              You don't have permission to access this page.
            </p>
          </div>
        </div>
      );
    }
  }

  // Wrap in local Suspense so lazy-loaded admin pages (AdminLayout,
  // AdminDashboard, etc.) suspend here rather than propagating to the
  // top-level Suspense in App.tsx. Without this, the top-level Suspense
  // unmounts and remounts the entire Routes tree on every lazy-chunk load,
  // resetting useAdminAuth's `loading` state and causing React error #300
  // during the post-auth → /admin transition.
  return <Suspense fallback={<AdminSpinner />}>{children}</Suspense>;
}
